import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import {
  faCircleXmark,
  faEllipsisVertical,
  faEye,
  faMoneyBill,
  faReply,
  faBan,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { CustomColDef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getWithdrawalStatusBadgeConfig,
  isWithdrawalMarkableAsPaid,
  isWithdrawalPendingReview,
} from '../config/withdrawalStatusDisplay';
import type { Withdrawal } from '../models';

const formatCurrency = (value?: number, currency?: string) => {
  if (value === undefined || value === null) return '—';
  return formatCurrencyAmount(value, currency || 'EUR');
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const getWithdrawalWalletBalance = (withdrawal: Withdrawal): number | null => {
  if (withdrawal.wallet_balance != null) return withdrawal.wallet_balance;
  if (withdrawal.wallet?.balance_total != null) return withdrawal.wallet.balance_total;
  return null;
};

const getStatusBadge = (status: string, t: TFunction<'withdrawalsModule'>) => {
  const config = getWithdrawalStatusBadgeConfig(status, t);
  return (
    <Badge variant={config.variant} className={`text-xs ${config.className ?? ''}`}>
      {config.label}
    </Badge>
  );
};

export type WithdrawalsColumnDef = ColumnDef<Withdrawal>;

export interface UseWithdrawalsColumnsOptions {
  onMarkPaid?: (withdrawal: Withdrawal) => void;
  onReject?: (withdrawal: Withdrawal) => void;
  onCancel?: (withdrawal: Withdrawal) => void;
  onMarkReturned?: (withdrawal: Withdrawal) => void;
  onViewDetail?: (withdrawal: Withdrawal) => void;
  selectedColumns?: string[];
}

const buildWithdrawalsColumns = (
  t: TFunction<'withdrawalsModule'>,
  options: UseWithdrawalsColumnsOptions,
  is3pl: boolean,
  isExternal: boolean,
  canApprove: boolean,
  currency: string,
): WithdrawalsColumnDef[] => {
  const { onMarkPaid, onReject, onCancel, onMarkReturned, onViewDetail } = options;

  const columns: WithdrawalsColumnDef[] = [
    {
      id: 'bank_account_id',
      accessorKey: 'bank_account_id',
      header: t('table.columns.bank_account'),
      cell: ({ row }) => {
        const bankAccount = row.original.bank_account;
        if (bankAccount) {
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium">{bankAccount.holder_name}</span>
              <span className="text-xs text-muted-foreground">{bankAccount.iban}</span>
            </div>
          );
        }
        return <span className="text-sm">#{row.original.bank_account_id}</span>;
      },
    },
    {
      id: 'wallet_balance',
      header: t('table.columns.wallet_balance'),
      cell: ({ row }) => {
        const balance = getWithdrawalWalletBalance(row.original);
        return (
          <span className="text-sm font-medium">{balance !== null ? formatCurrency(balance, currency) : '—'}</span>
        );
      },
    },
    {
      id: 'requested_amount',
      header: t('table.columns.requested_amount'),
      cell: ({ row }) => (
        <span className="text-sm font-medium">{formatCurrency(row.original.requested_amount, currency)}</span>
      ),
    },
    {
      id: 'approved_amount',
      header: t('table.columns.approved_amount'),
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.approved_amount !== null ? formatCurrency(row.original.approved_amount, currency) : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('table.columns.status'),
      cell: ({ row }) => getStatusBadge(row.original.status, t),
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: t('table.columns.created_at'),
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: 'bank_transfer_date',
      accessorKey: 'bank_transfer_date',
      header: t('table.columns.bank_transfer_date'),
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.bank_transfer_date)}</span>,
    },
  ];

  if (is3pl) {
    columns.push(
      {
        id: 'wallet_id',
        accessorKey: 'wallet_id',
        header: t('table.columns.client'),
        cell: ({ row }) => (
          <span className="text-sm">{row.original.wallet?.team?.name ?? `#${row.original.wallet_id}`}</span>
        ),
      },
      {
        id: 'requested_by_user_id',
        header: t('table.columns.requested_by'),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.requested_by_user?.name ??
              (row.original.requested_by_user_id ? `#${row.original.requested_by_user_id}` : '—')}
          </span>
        ),
      },
      {
        id: 'reviewed_by_user_id',
        header: t('table.columns.reviewed_by'),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.reviewed_by_user?.name ??
              (row.original.reviewed_by_user_id ? `#${row.original.reviewed_by_user_id}` : '—')}
          </span>
        ),
      },
      {
        id: 'processed_by_user_id',
        header: t('table.columns.processed_by'),
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.processed_by_user?.name ??
              (row.original.processed_by_user_id ? `#${row.original.processed_by_user_id}` : '—')}
          </span>
        ),
      },
    );
  }

  // Acciones
  columns.push({
    id: 'actions',
    header: '',
    enableSorting: false,
    ...({ minWidth: 50, maxWidth: 50 } as any),
    cell: ({ row }) => {
      const withdrawal = row.original;
      const isPending = isWithdrawalPendingReview(withdrawal.status);
      const canMarkPaid = isWithdrawalMarkableAsPaid(withdrawal.status);
      const isPaid = withdrawal.status === 'PAID';

      const actionItems: ReactNode[] = [];

      actionItems.push(
        <DropdownMenuItem key="view" onClick={() => onViewDetail?.(withdrawal)}>
          <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2" />
          {t('actions.view')}
        </DropdownMenuItem>,
      );

      if (canMarkPaid && is3pl && canApprove) {
        actionItems.push(
          <DropdownMenuItem key="mark-paid" onClick={() => onMarkPaid?.(withdrawal)}>
            <FontAwesomeIcon icon={faMoneyBill} className="w-4 h-4 mr-2 text-green-600" />
            {t('actions.markPaid')}
          </DropdownMenuItem>,
        );
      }

      if (isPending && is3pl && canApprove) {
        actionItems.push(
          <DropdownMenuItem key="reject" onClick={() => onReject?.(withdrawal)} className="text-destructive">
            <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 mr-2" />
            {t('actions.reject')}
          </DropdownMenuItem>,
        );
      }

      if (isPaid && is3pl) {
        actionItems.push(
          <DropdownMenuItem key="mark-returned" onClick={() => onMarkReturned?.(withdrawal)}>
            <FontAwesomeIcon icon={faReply} className="w-4 h-4 mr-2" />
            {t('actions.markReturned')}
          </DropdownMenuItem>,
        );
      }

      if (isPending && isExternal) {
        actionItems.push(
          <DropdownMenuItem key="cancel" onClick={() => onCancel?.(withdrawal)}>
            <FontAwesomeIcon icon={faBan} className="w-4 h-4 mr-2" />
            {t('actions.cancel')}
          </DropdownMenuItem>,
        );
      }

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 px-0" aria-label={t('table.columns.actions')}>
              <FontAwesomeIcon icon={faEllipsisVertical} className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            {actionItems.length ? actionItems : <DropdownMenuItem disabled>{t('noResults')}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  });

  return columns;
};

const mapToAgGridColumns = (columns: WithdrawalsColumnDef[]): CustomColDef[] => {
  return columns.map((column) => {
    const accessorKey =
      'accessorKey' in column && typeof column.accessorKey === 'string' ? column.accessorKey : undefined;

    const columnId = (column.id as string) || accessorKey || String((column as any).accessorKey ?? '');

    const isActionsColumn = columnId === 'actions';

    return {
      colId: columnId,
      field: accessorKey && !isActionsColumn ? accessorKey : undefined,
      headerName: typeof column.header === 'string' ? column.header : undefined,
      pinned: isActionsColumn ? 'right' : undefined,
      width: (column as any).size,
      minWidth: (column as any).minWidth,
      maxWidth: (column as any).maxWidth,
      sortable: column.enableSorting !== false,
      lockPosition: isActionsColumn ? 'right' : undefined,
      suppressMovable: isActionsColumn,
      suppressAutoSize: isActionsColumn,
      suppressSizeToFit: isActionsColumn,
      cellRenderer: (params: any) => {
        if (typeof column.cell !== 'function') {
          return params.value;
        }

        return column.cell({
          row: { original: params.data },
          getValue: () => params.value,
        } as any);
      },
    } as CustomColDef;
  });
};

export interface UseWithdrawalsColumnsResult {
  tanStackColumns: WithdrawalsColumnDef[];
  agGridColumnDefs: CustomColDef[];
  filteredColumnDefs: CustomColDef[];
}

export const useWithdrawalsColumns = (options?: UseWithdrawalsColumnsOptions): UseWithdrawalsColumnsResult => {
  const { t } = useTranslation('withdrawalsModule');
  const { canAccess } = usePermissions();
  const currency = useCurrency();
  const is3pl = canAccess(['tenant_3pl'], []);
  const isExternal = canAccess(['tenant_external'], []);
  const canApprove = canAccess(['tenant_3pl'], ['withdrawals.approve']);

  const tanStackColumns = useMemo<WithdrawalsColumnDef[]>(
    () => buildWithdrawalsColumns(t, options ?? {}, is3pl, isExternal, canApprove, currency),
    [t, options, is3pl, isExternal, canApprove, currency],
  );

  const agGridColumnDefs = useMemo(() => mapToAgGridColumns(tanStackColumns), [tanStackColumns]);

  const filteredColumnDefs = useMemo(() => {
    const selectedIds = options?.selectedColumns;
    if (!selectedIds || selectedIds.length === 0) {
      return agGridColumnDefs;
    }

    return agGridColumnDefs.filter((column) => selectedIds.includes(String(column.colId)));
  }, [agGridColumnDefs, options?.selectedColumns]);

  return {
    tanStackColumns,
    agGridColumnDefs,
    filteredColumnDefs,
  };
};
